import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/Panel";
import { Paginacion } from "@/components/Paginacion";
import { numTrabajo, fmtFecha, descargarPdf } from "@/pages/TrabajosActivos";
import { Search, Printer, FileText } from "lucide-react";

const FILTROS = [
  { value: "", label: "Todos" },
  { value: "activo", label: "Activos" },
  { value: "finalizado", label: "Finalizados" },
  { value: "anulado", label: "Anulados" },
];

const BADGE_ESTADO = {
  activo: "bg-indigo-50 text-indigo-700",
  finalizado: "bg-emerald-50 text-emerald-700",
  anulado: "bg-rose-50 text-rose-700",
};

const pendienteDe = (t) => {
  if (t.estado !== "activo") return null;
  const tareasPend = (t.tareas || []).filter((ta) => !ta.realizado || ta.aprobacion === "no_aprobado")
    .map((ta) => ta.servicio?.nombre).filter(Boolean);
  const repsPend = (t.repuestos || []).filter((r) => r.aprobado && !r.instalado).map((r) => r.nombre);
  return { tareasPend, repsPend };
};

export default function TrabajosPendientes() {
  const [trabajos, setTrabajos] = useState([]);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get("/trabajos", { params: { estado: estado || undefined, q: q.trim() || undefined, page, page_size: pageSize } });
      setTrabajos(r.data.items);
      setMeta({ total: r.data.total, total_pages: r.data.total_pages });
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setCargando(false); }
  }, [estado, q, page, pageSize]);
  useEffect(() => { setPage(1); }, [q, estado, pageSize]);
  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  return (
    <div data-testid="trabajos-pendientes-page" className="space-y-4">
      <Panel titulo="Boletas de taller" testId="trabajos-pendientes-panel">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-56">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="pendientes-buscar-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por número, placa o cliente..." className="pl-9" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTROS.map((f) => (
              <button key={f.value} type="button" data-testid={`pendientes-filtro-${f.value || "todos"}`}
                onClick={() => setEstado(f.value)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                  estado === f.value ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="pendientes-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold">N° de Orden</th>
                <th className="py-2.5 pr-3 font-semibold">Vehículo / Cliente</th>
                <th className="py-2.5 pr-3 font-semibold">Estado</th>
                <th className="py-2.5 pr-3 font-semibold">Pendiente</th>
                <th className="py-2.5 pr-3 font-semibold">Fecha de registro</th>
                <th className="py-2.5 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {trabajos.map((t) => {
                const pend = pendienteDe(t);
                return (
                  <tr key={t.id} data-testid={`pendiente-row-${t.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2.5 pr-3 font-medium text-slate-800">{numTrabajo(t.numero)}</td>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-slate-800">
                        {t.vehiculo ? `${t.vehiculo.marca} ${t.vehiculo.modelo} · ${t.vehiculo.placa}` : "—"}
                      </div>
                      <div className="text-xs text-slate-500">{t.cliente?.nombre || "—"}</div>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${BADGE_ESTADO[t.estado] || "bg-slate-100 text-slate-600"}`}>
                        {t.estado}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600" data-testid={`pendiente-resumen-${t.id}`}>
                      {!pend ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <>
                          {pend.tareasPend.length > 0 && (
                            <div>{pend.tareasPend.length} tarea{pend.tareasPend.length > 1 ? "s" : ""}: <span className="text-xs">{pend.tareasPend.join(", ")}</span></div>
                          )}
                          {pend.repsPend.length > 0 && (
                            <div className="text-xs text-slate-500">{pend.repsPend.length} repuesto{pend.repsPend.length > 1 ? "s" : ""} sin instalar</div>
                          )}
                          {pend.tareasPend.length === 0 && pend.repsPend.length === 0 && (
                            <span className="text-xs text-slate-400">Sin ítems pendientes</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">{fmtFecha(t.fecha_ingreso)}</td>
                    <td className="py-2.5">
                      <div className="flex justify-end gap-1.5">
                        <button data-testid={`pendiente-boleta-btn-${t.id}`} title="Boleta (PDF)"
                          onClick={() => descargarPdf(`/trabajos/${t.id}/boleta-pdf`, `boleta_${numTrabajo(t.numero)}.pdf`)}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100">
                          <FileText size={13} />
                        </button>
                        <button data-testid={`pendiente-reporte-btn-${t.id}`} title="Reporte para mecánicos (PDF)"
                          onClick={() => descargarPdf(`/trabajos/${t.id}/tareas-pdf`, `trabajos_${numTrabajo(t.numero)}.pdf`)}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                          <Printer size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {trabajos.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-400" data-testid="pendientes-vacio">
                  No hay trabajos que coincidan con el filtro
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginacion page={page} totalPages={meta.total_pages} total={meta.total} pageSize={pageSize}
          onPage={setPage} onPageSize={setPageSize} cargando={cargando} />
      </Panel>
    </div>
  );
}
